import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { fileteDasAbas } from '@/componentes/filete';
import { Janela, JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeDeContagem, RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { formatarData, formatarDataHora, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { SituacaoDePessoa } from '@/modulos/cadastros/Avisos';
import { enderecoDoConvite, situacaoDoAcesso } from '@/modulos/cadastros/convite';

import { api, type DetalheDaEmpresa, type PapelDaEmpresa, type PessoaGerida } from './api';
import { usePaginacao } from './paginacao';
import { AcaoComMotivo, EnderecoParaEntregar, Recado } from './Pecas';

/**
 * Uma empresa vista pela administração da plataforma (decisão 25, 0032).
 *
 * A plataforma gere o ACESSO da empresa — quem entra, com que papel, se o
 * convite está aberto, quantas sessões estão abertas — e não a OPERAÇÃO: frota,
 * O.S., combustível e custo continuam só para quem é de dentro. Cada ato fica
 * nas duas trilhas, e o que muda alguém exige motivo.
 */

const chaveDaEmpresa = (empresaId: string) => ['plataforma', 'empresa', empresaId] as const;

/** "1 sessão", "3 sessões": a contagem flexiona o número (p.22–23). */
function contagem(quantidade: number, um: string, muitos: string): string {
  return `${formatarInteiro(quantidade)} ${quantidade === 1 ? um : muitos}`;
}

interface Convite {
  readonly para: string;
  readonly codigo: string;
  readonly expiraEm: string;
  readonly troca: boolean;
}

type Aba = 'pessoas' | 'papeis';

export function GestaoDaEmpresa({ empresaId, aoVoltar }: { readonly empresaId: string; readonly aoVoltar: () => void }) {
  const cliente = useQueryClient();
  const [aba, setAba] = useState<Aba>('pessoas');
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [cadastrando, setCadastrando] = useState(false);
  const [convite, setConvite] = useState<Convite | null>(null);

  const detalhe = useQuery({ queryKey: chaveDaEmpresa(empresaId), queryFn: () => api.empresa(empresaId) });
  const atualizar = () => {
    void cliente.invalidateQueries({ queryKey: ['plataforma'] });
  };

  /* Retorno de ação é toast (R04, p.32): o sucesso diz o que aconteceu, na
     empresa, com o que mudou; a recusa sai pelo toast de falha da camada de
     chamadas. */
  const daEmpresa = () => (detalhe.data ? { alvo: { nome: detalhe.data.empresa.nome } } : {});
  const suporte = useMutation({
    mutationFn: (motivo: string) => api.abrirSuporte(empresaId, motivo),
    meta: {
      retorno: retornoDe<{ expiraEm: string }, string>((r, motivo) => ({
        titulo: 'Acesso de suporte aberto',
        ...daEmpresa(),
        fatos: [`até ${formatarDataHora(new Date(r.expiraEm))}`, `motivo: ${motivo}`],
        efeito: 'Abriu numa aba nova; se não abriu, o navegador bloqueou a janela: abra o endereço do sistema numa aba nova',
      })),
    },
    onSuccess: () => {
      window.open('/', '_blank', 'noopener');
      atualizar();
    },
  });
  const situacao = useMutation({
    mutationFn: (p: { readonly ativa: boolean; readonly motivo: string }) => api.situacaoDaEmpresa(empresaId, p.ativa, p.motivo),
    meta: {
      retorno: retornoDe<{ ativa: boolean; sessoesEncerradas: number; convitesAnulados: number }, { motivo: string }>((r, p) => ({
        titulo: r.ativa ? 'Empresa reativada' : 'Empresa desativada',
        ...daEmpresa(),
        fatos: [`motivo: ${p.motivo}`],
        efeito: r.ativa
          ? 'Quem tinha senha volta a entrar'
          : `${contagem(r.sessoesEncerradas, 'sessão encerrada', 'sessões encerradas')} e ${contagem(r.convitesAnulados, 'convite anulado', 'convites anulados')}`,
      })),
    },
    onSuccess: () => {
      atualizar();
    },
  });

  const d = detalhe.data;
  const paginaDosPapeis = usePaginacao(d?.papeis ?? []);
  const paginaDasPessoas = usePaginacao(d?.pessoas ?? []);
  const pessoa = d?.pessoas.find((p) => p.usuarioId === selecionada) ?? null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <div>
            <Botao nivel="terciario" onClick={aoVoltar}>
              Todas as empresas
            </Botao>
          </div>
          {detalhe.isPending ? <CarregandoAEmpresa /> : null}
          {detalhe.isError ? (
            <ErroDaLista
              oQue="Os dados da empresa"
              erro={detalhe.error}
              aoTentarDeNovo={() => {
                void detalhe.refetch();
              }}
            />
          ) : null}
          {d ? (
            <>
              <CabecalhoPagina
                sobretitulo="Empresa"
                titulo={d.empresa.nome}
                subtitulo={`No sistema desde ${formatarData(new Date(d.empresa.criadoEm))}. Daqui se gere quem entra; o que há dentro, só quem é de dentro vê.`}
                acoes={
                  <>
                    <AcaoComMotivo
                      rotulo="Abrir como suporte"
                      pergunta={`Abrir ${d.empresa.nome} como suporte?`}
                      consequencia="Abre o sistema desta empresa numa aba nova, só para leitura, por 2 horas. Cada tela aberta fica na trilha dela. Neste navegador, a sessão de empresa que houver é trocada pela de suporte."
                      rotuloConfirmar="Abrir como suporte"
                      pendente={suporte.isPending}
                      aoConfirmar={(m) => {
                        suporte.mutate(m);
                      }}
                    />
                    <Renomear detalhe={d} aoConcluir={atualizar} />
                    <AcaoComMotivo
                      rotulo={d.empresa.ativa ? 'Desativar empresa' : 'Reativar empresa'}
                      nivel={d.empresa.ativa ? 'destrutivo' : 'secundario'}
                      pergunta={d.empresa.ativa ? `Desativar ${d.empresa.nome}?` : `Reativar ${d.empresa.nome}?`}
                      consequencia={
                        d.empresa.ativa
                          ? 'Ninguém desta empresa entra mais: as sessões caem agora e os convites abertos são anulados. Os dados ficam guardados, e reativar devolve a entrada.'
                          : 'Quem tinha senha volta a entrar. Convites anulados na desativação precisam ser emitidos de novo.'
                      }
                      rotuloConfirmar={d.empresa.ativa ? 'Desativar empresa' : 'Reativar empresa'}
                      pendente={situacao.isPending}
                      aoConfirmar={(m) => {
                        situacao.mutate({ ativa: !d.empresa.ativa, motivo: m });
                      }}
                    />
                    <Botao
                      nivel="primario"
                      onClick={() => {
                        setCadastrando(true);
                        setAba('pessoas');
                      }}
                    >
                      Cadastrar pessoa
                    </Botao>
                  </>
                }
              />

              {!d.empresa.ativa ? (
                <div role="status" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
                  <span className="txt-corpo text-tinta">Empresa desativada</span>
                  <span className="txt-apoio text-tinta-secundaria">
                    Ninguém dela entra, e nenhum convite dela é aceito. Os dados continuam guardados. Para devolver a entrada, use Reativar empresa.
                  </span>
                </div>
              ) : null}

              <FaixaIndicadores>
                <CardIndicador
                  rotulo="Pessoas ativas"
                  valor={formatarInteiro(d.empresa.pessoasAtivas)}
                  contexto={`de ${contagem(d.empresa.pessoas, 'cadastrada', 'cadastradas')}`}
                  icone={Users}
                  tom="operando"
                  {...(d.empresa.pessoas > 0 ? { proporcao: d.empresa.pessoasAtivas / d.empresa.pessoas } : {})}
                />
                <CardIndicador
                  rotulo="Entram no sistema"
                  valor={formatarInteiro(d.empresa.comAcesso)}
                  contexto="com senha própria"
                  icone={Users}
                />
                <CardIndicador
                  rotulo="Convites abertos"
                  valor={formatarInteiro(d.empresa.convitesAbertos)}
                  contexto="ainda não aceitos, dentro do prazo"
                  icone={Users}
                  tom="standby"
                />
                <CardIndicador
                  rotulo="Sessões abertas"
                  valor={formatarInteiro(d.empresa.sessoesAbertas)}
                  contexto="agora, somando todas as pessoas"
                  icone={Users}
                />
              </FaixaIndicadores>

              {convite ? (
                <EnderecoParaEntregar
                  titulo={convite.troca ? 'Troca de senha emitida' : 'Convite emitido'}
                  endereco={enderecoDoConvite(window.location.origin, convite.codigo)}
                  para={convite.para}
                  expiraEm={convite.expiraEm}
                  depois={convite.troca ? 'Ao aceitar, a senha antiga deixa de valer e as sessões abertas caem.' : 'Quem abrir escolhe a senha e entra na empresa.'}
                  aoFechar={() => {
                    setConvite(null);
                  }}
                />
              ) : null}

              {/* Cadastrar pessoa é tarefa curta (três campos): abre em janela
                  (R01, p.29), e o laranja que se alcança com ela aberta é o de
                  cadastrar, dentro dela. */}
              <Janela
                titulo="Cadastrar pessoa"
                aberta={cadastrando}
                aoFechar={() => {
                  setCadastrando(false);
                }}
              >
                <CadastroDePessoa
                  empresaId={empresaId}
                  papeis={d.papeis}
                  aoCancelar={() => {
                    setCadastrando(false);
                  }}
                  aoCadastrar={(c) => {
                    setCadastrando(false);
                    setConvite(c);
                    atualizar();
                  }}
                />
              </Janela>

              <div role="tablist" aria-label="Seções da empresa" ref={fileteDasAbas} className="barra-de-abas filete-das-abas">
                {(
                  [
                    ['pessoas', `Pessoas (${formatarInteiro(d.pessoas.length)})`],
                    ['papeis', `Papéis (${formatarInteiro(d.papeis.length)})`],
                  ] as const
                ).map(([id, rotulo]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={aba === id}
                    onClick={() => {
                      setAba(id);
                    }}
                    className="aba"
                  >
                    {rotulo}
                  </button>
                ))}
              </div>

              {aba === 'pessoas' ? (
                <div data-painel-origem>
                  <Tabela
                    rotulo={`Pessoas de ${d.empresa.nome}`}
                    colunas={COLUNAS_DE_PESSOA}
                    linhas={paginaDasPessoas.visiveis}
                    rodape={<RodapeTabela {...paginaDasPessoas.rodape} />}
                    chaveDaLinha={(p) => p.usuarioId}
                    alturaDupla
                    selecionada={selecionada}
                    aoSelecionar={(p) => {
                      setSelecionada(p.usuarioId);
                    }}
                    vazio={
                      <EstadoVazio
                        titulo="Nenhuma pessoa nesta empresa"
                        descricao="Cadastre a primeira pessoa: ela recebe um convite e escolhe a própria senha."
                        acao={
                          <Botao
                            nivel="secundario"
                            onClick={() => {
                              setCadastrando(true);
                            }}
                          >
                            Cadastrar pessoa
                          </Botao>
                        }
                      />
                    }
                  />
                </div>
              ) : (
                <Tabela
                  rotulo={`Papéis de ${d.empresa.nome}`}
                  colunas={COLUNAS_DE_PAPEL}
                  linhas={paginaDosPapeis.visiveis}
                  rodape={<RodapeTabela {...paginaDosPapeis.rodape} />}
                  chaveDaLinha={(p) => p.papelId}
                  vazio={
                    <EstadoVazio
                      titulo="Nenhum papel ativo nesta empresa"
                      descricao="Os papéis e as permissões de cada um são desenhados por quem administra a empresa, na governança dela."
                    />
                  }
                />
              )}
            </>
          ) : null}
        </div>
      </div>

      {d && pessoa ? (
        <PainelDaPessoa
          key={pessoa.usuarioId}
          empresaId={empresaId}
          pessoa={pessoa}
          papeis={d.papeis}
          aoConvidar={setConvite}
          aoMudar={atualizar}
          aoFechar={() => {
            setSelecionada(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Carregando (p.32) na forma da empresa: o sobretítulo, o rótulo dos quatro
 * indicadores, o nome das abas e o cabeçalho da tabela já se sabem; o nome, os
 * números e as pessoas viram esqueleto.
 */
function CarregandoAEmpresa() {
  return (
    <>
      <Carregando oQue="Carregando a empresa" className="flex min-w-0 flex-col gap-1">
        <span aria-hidden="true" className="txt-sobretitulo text-tinta-apagada">
          Empresa
        </span>
        <Barra className="my-1 h-7 w-64 max-w-full" />
        <Barra className="my-1 h-3 w-3/4" />
      </Carregando>
      <FaixaIndicadores>
        <CardIndicador carregando rotulo="Pessoas ativas" valor="" contexto="" icone={Users} tom="operando" />
        <CardIndicador carregando rotulo="Entram no sistema" valor="" contexto="" icone={Users} />
        <CardIndicador carregando rotulo="Convites abertos" valor="" contexto="" icone={Users} tom="standby" />
        <CardIndicador carregando rotulo="Sessões abertas" valor="" contexto="" icone={Users} />
      </FaixaIndicadores>
      <div aria-hidden="true" className="barra-de-abas">
        {['Pessoas', 'Papéis'].map((rotulo) => (
          <span key={rotulo} className="aba">
            {rotulo}
            <Barra className="h-3 w-6" />
          </span>
        ))}
      </div>
      <Tabela
        rotulo="Pessoas da empresa"
        colunas={COLUNAS_DE_PESSOA}
        linhas={[]}
        chaveDaLinha={(p) => p.usuarioId}
        alturaDupla
        carregando
        /* O esqueleto reserva o rodapé que a tabela vai ter: nada pula (p.32). */
        rodape={<RodapeDeContagem total={0} />}
        vazio={null}
      />
    </>
  );
}

const acessoDe = (p: PessoaGerida) =>
  situacaoDoAcesso({ ativo: p.ativo, email: p.email, tem_acesso: p.temAcesso, convite_ate: p.conviteAte }, new Date());

const COLUNAS_DE_PESSOA: readonly Coluna<PessoaGerida>[] = [
  {
    chave: 'pessoa',
    rotulo: 'Pessoa',
    celula: (p) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{p.nome}</span>
        <span className="txt-apoio text-tinta-secundaria">{p.email ?? 'sem e-mail'}</span>
      </span>
    ),
  },
  {
    chave: 'papeis',
    rotulo: 'Papéis',
    celula: (p) =>
      p.papeis.length > 0 ? (
        <span className="txt-corpo text-tinta">{p.papeis.map((x) => x.nome).join(', ')}</span>
      ) : (
        <span className="txt-apoio text-tinta-secundaria">Sem papel: entra e não abre nada</span>
      ),
  },
  {
    chave: 'acesso',
    rotulo: 'Acesso',
    celula: (p) => {
      const a = acessoDe(p);
      return (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{a.rotulo}</span>
          <span className="txt-apoio text-tinta-secundaria">{a.detalhe}</span>
        </span>
      );
    },
  },
  {
    chave: 'sessoes',
    rotulo: 'Sessões abertas',
    alinhamento: 'numero',
    celula: (p) => formatarInteiro(p.sessoesAbertas),
  },
  { chave: 'situacao', rotulo: 'Situação', alinhamento: 'status', celula: (p) => <SituacaoDePessoa ativo={p.ativo} /> },
];

const COLUNAS_DE_PAPEL: readonly Coluna<PapelDaEmpresa>[] = [
  { chave: 'nome', rotulo: 'Papel', celula: (p) => p.nome },
  { chave: 'codigo', rotulo: 'Código', celula: (p) => <span className="codigo">{p.codigo}</span> },
  { chave: 'pessoas', rotulo: 'Pessoas', alinhamento: 'numero', celula: (p) => formatarInteiro(p.pessoas) },
];

/**
 * Renomear a empresa: dois campos, em janela (R01, p.29). Antes os campos
 * abriam DENTRO do grupo de ações do cabeçalho e o empurravam para fora.
 */
function Renomear({ detalhe, aoConcluir }: { readonly detalhe: DetalheDaEmpresa; readonly aoConcluir: () => void }) {
  const [abrindo, setAbrindo] = useState(false);
  return (
    <JanelaDeAcao
      rotulo="Renomear empresa"
      titulo="Renomear empresa"
      aberta={abrindo}
      aoAbrir={() => {
        setAbrindo(true);
      }}
      aoFechar={() => {
        setAbrindo(false);
      }}
    >
      <NomeNovo
        detalhe={detalhe}
        aoCancelar={() => {
          setAbrindo(false);
        }}
        aoConcluir={() => {
          setAbrindo(false);
          aoConcluir();
        }}
      />
    </JanelaDeAcao>
  );
}

/** O corpo da janela de renomear: montado a cada abertura, com o nome de agora. */
function NomeNovo({
  detalhe,
  aoCancelar,
  aoConcluir,
}: {
  readonly detalhe: DetalheDaEmpresa;
  readonly aoCancelar: () => void;
  readonly aoConcluir: () => void;
}) {
  const [nome, setNome] = useState(detalhe.empresa.nome);
  const [motivo, setMotivo] = useState('');
  const renomear = useMutation({
    mutationFn: () => api.renomearEmpresa(detalhe.empresa.empresaId, nome.trim(), motivo.trim()),
    /* A recusa fica no aviso do topo do formulário (p.22), e não repete no toast. */
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Empresa renomeada',
        alvo: { nome: nome.trim() },
        fatos: [`motivo: ${motivo.trim()}`],
        efeito: { rotulo: 'Nome', antes: detalhe.empresa.nome, depois: nome.trim() },
      })),
      silenciarFalha: true,
    },
    onSuccess: aoConcluir,
  });
  function enviar(evento: FormEvent) {
    evento.preventDefault();
    renomear.mutate();
  }
  return (
    <form className="formulario-grade" onSubmit={enviar}>
      {renomear.isError ? (
        <div className="campo-linha">
          <Recado erro={explicarFalha(renomear.error)} />
        </div>
      ) : null}
      <div className="campo-largo">
        <CampoTexto rotulo="Nome novo" valor={nome} maxLength={200} aoMudar={setNome} />
      </div>
      <div className="campo-linha">
        <CampoTexto
          rotulo="Motivo"
          valor={motivo}
          maxLength={300}
          ajuda="Vai para a trilha da plataforma e para a da empresa."
          aoMudar={setMotivo}
        />
      </div>
      <div className="barra-de-acoes campo-linha">
        <Botao nivel="secundario" onClick={aoCancelar}>
          Manter o nome
        </Botao>
        <Botao nivel="primario" type="submit" disabled={renomear.isPending || nome.trim() === '' || motivo.trim() === ''}>
          Gravar nome
        </Botao>
      </div>
    </form>
  );
}

function CadastroDePessoa({
  empresaId,
  papeis,
  aoCancelar,
  aoCadastrar,
}: {
  readonly empresaId: string;
  readonly papeis: readonly PapelDaEmpresa[];
  readonly aoCancelar: () => void;
  readonly aoCadastrar: (convite: Convite) => void;
}) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState('');
  const cadastrar = useMutation({
    mutationFn: () => api.cadastrarPessoa(empresaId, { nome: nome.trim(), email: email.trim(), papelId: papel || null }),
    meta: {
      retorno: retornoDe<{ expiraEm: string }>((r) => ({
        titulo: 'Pessoa cadastrada e convite emitido',
        alvo: { nome: nome.trim() },
        fatos: [email.trim().toLowerCase(), papeis.find((p) => p.papelId === papel)?.nome ?? null],
        efeito: `O convite vale até ${formatarDataHora(new Date(r.expiraEm))}`,
      })),
      silenciarFalha: true,
    },
    onSuccess: (r) => {
      aoCadastrar({ para: email.trim().toLowerCase(), codigo: r.codigo, expiraEm: r.expiraEm, troca: false });
    },
  });
  function enviar(evento: FormEvent) {
    evento.preventDefault();
    cadastrar.mutate();
  }
  return (
    <form className="formulario-grade" onSubmit={enviar}>
      {cadastrar.isError ? (
        <div className="campo-linha">
          <Recado erro={explicarFalha(cadastrar.error)} />
        </div>
      ) : null}
      <div className="campo-largo">
        <CampoTexto rotulo="Nome" valor={nome} maxLength={120} aoMudar={setNome} />
      </div>
      <CampoTexto
        rotulo="E-mail"
        valor={email}
        tipo="email"
        maxLength={320}
        ajuda="É o que a pessoa digita para entrar. O convite sai para ele."
        aoMudar={setEmail}
      />
      <CampoSelecao
        rotulo="Papel"
        valor={papel}
        opcional
        rotuloVazio="Sem papel por enquanto"
        opcoes={papeis.map((p) => ({ valor: p.papelId, rotulo: p.nome }))}
        aoMudar={setPapel}
      />
      <div className="barra-de-acoes campo-linha">
        <Botao nivel="secundario" onClick={aoCancelar}>
          Voltar sem cadastrar
        </Botao>
        <Botao nivel="primario" type="submit" disabled={cadastrar.isPending || nome.trim() === '' || email.trim() === ''}>
          {cadastrar.isPending ? 'Cadastrando…' : 'Cadastrar e convidar'}
        </Botao>
      </div>
    </form>
  );
}

function PainelDaPessoa({
  empresaId,
  pessoa,
  papeis,
  aoConvidar,
  aoMudar,
  aoFechar,
}: {
  readonly empresaId: string;
  readonly pessoa: PessoaGerida;
  readonly papeis: readonly PapelDaEmpresa[];
  readonly aoConvidar: (c: Convite) => void;
  readonly aoMudar: () => void;
  readonly aoFechar: () => void;
}) {
  const [papelNovo, setPapelNovo] = useState('');
  /* Retorno de ação é toast (R04, p.32), e não uma frase na nota do painel:
     cada escrita declara o seu; a recusa sai pelo toast de falha da camada
     de chamadas. */
  const alvo = { nome: pessoa.nome };

  const convidar = useMutation({
    mutationFn: () => api.conviteDaPessoa(empresaId, pessoa.usuarioId),
    meta: {
      retorno: retornoDe<{ email: string; expiraEm: string }>((r) => ({
        titulo: pessoa.temAcesso ? 'Troca de senha emitida' : 'Convite emitido',
        alvo,
        fatos: [r.email],
        efeito: `Vale até ${formatarDataHora(new Date(r.expiraEm))}; o anterior deixou de valer`,
      })),
    },
    onSuccess: (r) => {
      aoConvidar({ para: r.email, codigo: r.codigo, expiraEm: r.expiraEm, troca: pessoa.temAcesso });
      aoMudar();
    },
  });
  const encerrar = useMutation({
    mutationFn: (motivo: string) => api.encerrarSessoes(empresaId, pessoa.usuarioId, motivo),
    meta: {
      retorno: retornoDe<{ encerradas: number }, string>((r, motivo) => ({
        titulo: 'Sessões encerradas',
        alvo,
        fatos: [contagem(r.encerradas, 'sessão encerrada', 'sessões encerradas'), `motivo: ${motivo}`],
      })),
    },
    onSuccess: aoMudar,
  });
  const situacao = useMutation({
    mutationFn: (motivo: string) => api.situacaoDaPessoa(empresaId, pessoa.usuarioId, !pessoa.ativo, motivo),
    meta: {
      retorno: retornoDe<{ ativo: boolean; sessoesEncerradas: number }, string>((r, motivo) => ({
        titulo: r.ativo ? 'Pessoa reativada' : 'Pessoa desativada',
        alvo,
        fatos: [`motivo: ${motivo}`],
        ...(r.ativo ? {} : { efeito: contagem(r.sessoesEncerradas, 'sessão encerrada', 'sessões encerradas') }),
      })),
    },
    onSuccess: aoMudar,
  });
  const papel = useMutation({
    mutationFn: (p: { readonly papelId: string; readonly conceder: boolean; readonly motivo: string }) =>
      api.papelDaPessoa(empresaId, pessoa.usuarioId, p.papelId, p.conceder, p.motivo),
    meta: {
      retorno: retornoDe<unknown, { papelId: string; conceder: boolean; motivo: string }>((_r, p) => ({
        titulo: p.conceder ? 'Papel concedido' : 'Papel retirado',
        alvo,
        fatos: [papeis.find((x) => x.papelId === p.papelId)?.nome ?? null, `motivo: ${p.motivo}`],
      })),
    },
    onSuccess: () => {
      setPapelNovo('');
      aoMudar();
    },
  });

  const acesso = acessoDe(pessoa);
  const jaTem = new Set(pessoa.papeis.map((p) => p.id));
  const disponiveis = papeis.filter((p) => !jaTem.has(p.papelId));
  const podeConvidar = pessoa.ativo && pessoa.email !== null;
  const nota = [
    !pessoa.ativo ? 'Pessoa desativada não recebe convite: reative-a primeiro.' : null,
    pessoa.ativo && pessoa.email === null ? 'Sem e-mail não há convite: quem administra a empresa grava o e-mail na governança dela.' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <PainelDetalhe
      titulo={pessoa.nome}
      subtitulo={pessoa.email ?? 'Sem e-mail cadastrado'}
      marcadores={<SituacaoDePessoa ativo={pessoa.ativo} />}
      dadosChave={
        <>
          <DadoChave rotulo="Acesso">{acesso.rotulo}</DadoChave>
          <DadoChave rotulo="Sessões abertas">
            <span className="tabular-nums">{formatarInteiro(pessoa.sessoesAbertas)}</span>
          </DadoChave>
          <DadoChave rotulo="Cadastrada em">{formatarData(new Date(pessoa.criadoEm))}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'acesso',
          rotulo: 'Acesso',
          conteudo: (
            <div className="flex flex-col gap-4">
              <p className="txt-apoio text-tinta-secundaria">{acesso.detalhe}</p>
              <p className="txt-apoio text-tinta-secundaria">
                {pessoa.temAcesso
                  ? 'A troca de senha é um convite novo: a pessoa abre o endereço e escolhe a senha. Ninguém mais a conhece, nem a plataforma.'
                  : 'O convite leva a pessoa a escolher a própria senha. Ninguém mais a conhece, nem a plataforma.'}
              </p>
              {pessoa.sessoesAbertas > 0 ? (
                <AcaoComMotivo
                  rotulo="Encerrar sessões"
                  pergunta={`Encerrar as sessões de ${pessoa.nome}?`}
                  consequencia={`${pessoa.sessoesAbertas === 1 ? '1 sessão cai' : `${formatarInteiro(pessoa.sessoesAbertas)} sessões caem`} agora. A pessoa continua podendo entrar com a senha dela.`}
                  rotuloConfirmar="Encerrar sessões"
                  pendente={encerrar.isPending}
                  aoConfirmar={(m) => {
                    encerrar.mutate(m);
                  }}
                />
              ) : null}
            </div>
          ),
        },
        {
          id: 'papeis',
          rotulo: 'Papéis',
          conteudo: (
            <div className="flex flex-col gap-4">
              {pessoa.papeis.length === 0 ? (
                <p className="txt-apoio text-tinta-secundaria">Sem papel: a pessoa entra e não abre módulo nenhum.</p>
              ) : (
                <ul className="flex flex-col">
                  {pessoa.papeis.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 border-b border-borda-sutil py-2">
                      <span className="txt-corpo text-tinta">{p.nome}</span>
                      <AcaoComMotivo
                        rotulo="Tirar papel"
                        nivel="terciario"
                        pergunta={`Tirar o papel ${p.nome} de ${pessoa.nome}?`}
                        consequencia="As permissões deste papel deixam de valer para a pessoa na próxima página que ela abrir."
                        rotuloConfirmar="Tirar papel"
                        pendente={papel.isPending}
                        aoConfirmar={(m) => {
                          papel.mutate({ papelId: p.id, conceder: false, motivo: m });
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
              {disponiveis.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <CampoSelecao
                    rotulo="Conceder papel"
                    valor={papelNovo}
                    rotuloVazio="Escolha o papel"
                    opcoes={disponiveis.map((p) => ({ valor: p.papelId, rotulo: p.nome }))}
                    aoMudar={setPapelNovo}
                  />
                  {papelNovo ? (
                    <AcaoComMotivo
                      rotulo="Conceder papel"
                      pergunta={`Conceder ${disponiveis.find((p) => p.papelId === papelNovo)?.nome ?? 'o papel'} a ${pessoa.nome}?`}
                      consequencia="As permissões do papel passam a valer na próxima página que a pessoa abrir."
                      rotuloConfirmar="Conceder papel"
                      pendente={papel.isPending}
                      aoConfirmar={(m) => {
                        papel.mutate({ papelId: papelNovo, conceder: true, motivo: m });
                      }}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          ),
        },
        {
          id: 'situacao',
          rotulo: 'Situação',
          conteudo: (
            <div className="flex flex-col gap-4">
              <p className="txt-apoio text-tinta-secundaria">
                {pessoa.ativo
                  ? 'Desativar derruba as sessões abertas e anula o convite pendente. O histórico da pessoa continua na empresa.'
                  : 'Reativada, a pessoa volta a entrar com a senha que já tinha. Se não tiver, emita o convite.'}
              </p>
              <AcaoComMotivo
                rotulo={pessoa.ativo ? 'Desativar pessoa' : 'Reativar pessoa'}
                nivel={pessoa.ativo ? 'destrutivo' : 'secundario'}
                pergunta={pessoa.ativo ? `Desativar ${pessoa.nome}?` : `Reativar ${pessoa.nome}?`}
                consequencia={
                  pessoa.ativo
                    ? 'A pessoa sai do sistema agora e não entra de novo até ser reativada.'
                    : 'A pessoa volta a poder entrar na empresa.'
                }
                rotuloConfirmar={pessoa.ativo ? 'Desativar pessoa' : 'Reativar pessoa'}
                pendente={situacao.isPending}
                aoConfirmar={(m) => {
                  situacao.mutate(m);
                }}
              />
            </div>
          ),
        },
      ]}
      acoes={[
        <Botao
          key="convite"
          nivel="secundario"
          disabled={!podeConvidar || convidar.isPending}
          onClick={() => {
            convidar.mutate();
          }}
        >
          {pessoa.temAcesso ? 'Emitir troca de senha' : 'Emitir convite'}
        </Botao>,
      ]}
      {...(nota ? { notaDasAcoes: nota } : {})}
      aoFechar={aoFechar}
    />
  );
}
